import { useState } from "react";
import { ChevronRight, Check, Plus } from "lucide-react";

/**
 * Renders `possible_variants` as clickable pill buttons.
 * - Click a pill to expand/collapse its confirmation details.
 * - Inside the details, click "Add to my car" to mark this variant as
 *   confirmed for the current owner (persists in garage when saved).
 * Multiple pills can be open and multiple variants can be selected.
 */
export const PossibleVariants = ({
  variants,
  title = "Possible Option Packages For This Model + Year",
  subtitle,
  selected,          // Set<string> of variant names the user has confirmed
  onToggleSelect,    // (name: string) => void
}) => {
  const [openIdx, setOpenIdx] = useState(new Set());

  if (!variants || variants.length === 0) return null;

  const toggle = (i) => {
    setOpenIdx((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const isSelected = (name) => Boolean(selected && selected.has(name));

  return (
    <div data-testid="possible-variants" className="mt-4 border border-shop-strong bg-elevated px-4 py-3">
      {title && (
        <div className="font-mono text-[10px] uppercase tracking-widest text-amber-brass">
          {title}
        </div>
      )}
      <p className="text-xs text-secondary mt-1 mb-3">
        {subtitle || "These were available options that aren't encoded in the VIN. Tap one to see how to confirm."}
        {onToggleSelect && (
          <span className="ml-1 text-amber-brass">Mark the ones your car has to save them into your garage.</span>
        )}
      </p>

      <div className="flex flex-wrap gap-2">
        {variants.map((pv, i) => {
          const isOpen = openIdx.has(i);
          const sel = isSelected(pv.name);
          return (
            <button
              key={i}
              type="button"
              data-testid={`variant-pill-${i}`}
              onClick={() => toggle(i)}
              className={`font-display uppercase tracking-wider text-sm px-3 py-1.5 border transition-all flex items-center gap-1.5 ${
                sel
                  ? "bg-[var(--accent-red)] text-cream border-[var(--accent-red)] shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
                  : isOpen
                    ? "bg-[var(--accent-amber)] text-shop border-[var(--accent-amber)] shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
                    : "bg-panel text-cream border-shop-strong hover:border-[var(--accent-amber)] hover:text-amber-brass"
              }`}
            >
              {sel ? (
                <Check size={12} />
              ) : (
                <ChevronRight
                  size={12}
                  className={`transition-transform ${isOpen ? "rotate-90" : ""}`}
                />
              )}
              {pv.name}
            </button>
          );
        })}
      </div>

      {[...openIdx].sort((a, b) => a - b).map((i) => {
        const pv = variants[i];
        if (!pv) return null;
        const sel = isSelected(pv.name);
        return (
          <div
            key={`detail-${i}`}
            data-testid={`variant-detail-${i}`}
            className="mt-3 border-l-2 border-[var(--accent-amber)] bg-panel/60 pl-3 py-2 pr-2"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-display uppercase text-base text-cream">{pv.name}</div>
              {onToggleSelect && (
                <button
                  type="button"
                  data-testid={`variant-add-${i}`}
                  onClick={() => onToggleSelect(pv.name)}
                  className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border flex items-center gap-1 transition-colors ${
                    sel
                      ? "border-[var(--accent-red)] text-[var(--accent-red)] hover:bg-[var(--accent-red)] hover:text-cream"
                      : "border-[var(--accent-amber)] text-amber-brass hover:bg-[var(--accent-amber)] hover:text-shop"
                  }`}
                >
                  {sel ? <><Check size={11} /> On my car — remove</> : <><Plus size={11} /> Add to my car</>}
                </button>
              )}
            </div>
            {pv.confirmation_source && (
              <div className="text-xs text-secondary mt-1">
                <span className="font-mono text-amber-brass">Confirm via:</span> {pv.confirmation_source}
              </div>
            )}
            {pv.features_to_look_for && (
              <div className="text-xs text-secondary mt-1">
                <span className="font-mono text-amber-brass">Look for:</span> {pv.features_to_look_for}
              </div>
            )}
            {pv.rarity && (
              <div className="text-xs text-secondary mt-1">
                <span className="font-mono text-amber-brass">Rarity:</span> {pv.rarity}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PossibleVariants;
